import { useParams } from 'react-router-dom'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { moduleByKey } from '@/lib/screens'

/**
 * A17 Infratuzilma — data-driven skeleton for the ASPIRATIONAL screens that have
 * no real data source on MyGram's single-node deploy. Multi-DC (server list /
 * data-centers / DC load / DC assignment / migration control), Laravel Horizon
 * (MyGram's stack is Go — no Horizon; the live media queue is A17.18), slow-query /
 * API-latency observability, external CDN, and the backup / restore / deploy /
 * rollback pipelines all require infra MyGram does not run today. Honest skeleton:
 * navigable, does NOT count as "live". One component reads :id (like FinancePlanned).
 */
const BLOCKER: Record<string, string> = {
  // Multi-DC (aspirational — MyGram is a single-node deploy)
  'A17.02': 'Реестр серверов + агенты хостов (MyGram — одноузловой деплой)',
  'A17.03': 'Многодатацентровая инфраструктура (MyGram — один ДЦ)',
  'A17.04': 'Балансировщик ДЦ + метрики нагрузки по зонам',
  'A17.05': 'Гео-шардинг: привязка пользователей к ДЦ',
  'A17.06': 'Оркестратор миграции пользователей между ДЦ',
  // Queue framework
  'A17.07': 'Laravel Horizon (стек MyGram на Go — Horizon нет; живая очередь — A17.18)',
  // Observability exporters
  'A17.11': 'Экспортёр медленных запросов (pg_stat_statements)',
  'A17.12': 'Экспортёр латентности API (/metrics — Prometheus, отдельный дашборд)',
  // External / pipeline
  'A17.17': 'Внешний CDN (Cloudflare) — API-эндпоинт отсутствует',
  'A17.19': 'Бэкап-оркестратор с API (сейчас — server-cron→B2, без эндпоинта)',
  'A17.20': 'Оркестратор восстановления (restore drill)',
  'A17.21': 'Деплой-конвейер: журнал релизов',
  'A17.22': 'Деплой-конвейер: откат релиза (rollback)',
}

function SkeletonBox({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-lg border border-dashed border-border-default bg-surface-2 ${className}`} />
  )
}

export function InfraPlanned() {
  const t = useT()
  const { id = '' } = useParams()
  const screen = moduleByKey('A17')?.screens.find((s) => s.id === id)
  const title = screen ? `${id} · ${screen.title}` : id
  const blocker = BLOCKER[id] ?? t.skeleton.needs
  return (
    <>
      <PageHeader title={title} screenId={id} />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="flex items-start gap-3 rounded-lg border border-border-default bg-surface-2 p-3">
          <span aria-hidden className="mt-0.5 shrink-0 font-mono text-accent">
            ▲
          </span>
          <div>
            <p className="text-sm font-medium text-primary">{t.skeleton.bannerTitle}</p>
            <p className="mt-0.5 text-sm text-secondary">
              {t.skeleton.needs}: <span className="text-primary">{blocker}</span>
            </p>
            <p className="mt-1 text-xs text-tertiary">{t.skeleton.adapterReady}</p>
          </div>
        </div>
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <SkeletonBox key={i} className="h-16" />
          ))}
        </div>
        <SkeletonBox className="h-40 max-w-3xl" />
      </div>
    </>
  )
}
